// frontend/src/components/mcbim/RoomColoringPieChart.tsx
import React, { useMemo } from "react";
import { Box, Group, Stack, Text } from "@mantine/core";
import { AgCharts } from "ag-charts-react";
import "ag-charts-enterprise";
import type { AgChartOptions, AgPieSeriesOptions } from "ag-charts-enterprise";

import { useAppSelector } from "../app/hooks";
import { selectRoomColoringChartData } from "./roomColoringSelectors";
import { selectActiveRoomColoring } from "../Room/roomSelectors";

type PieDatum = {
  label: string;
  value: number;
  color: string;
};

export const RoomColoringPieChart: React.FC = () => {
  const activeRoomColoring = useAppSelector(selectActiveRoomColoring);
  const { chartData, totalCount } = useAppSelector(selectRoomColoringChartData);

  const PIE_SIZE = 150;

  const options = useMemo<AgChartOptions | null>(() => {
    if (!activeRoomColoring || chartData.length === 0) return null;

    const pieSeries: AgPieSeriesOptions = {
      type: "pie",
      angleKey: "value",
      calloutLabelKey: "label",
      calloutLabel: { enabled: false },
      fills: chartData.map((d) => d.color),
      strokes: chartData.map(() => "#ffffff"),
      strokeWidth: 2,
      //innerRadiusRatio: 0.55,
    };

    return {
      data: chartData,
      background: { fill: "transparent" },
      legend: { enabled: false },
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
      width: PIE_SIZE,
      height: PIE_SIZE,
      series: [pieSeries],
    };
  }, [activeRoomColoring, chartData]);

  if (!activeRoomColoring) {
    return (
      <Text size="xs" c="dimmed">
        Ingen romfargelegging er aktiv.
      </Text>
    );
  }

  if (!options || totalCount === 0) {
    return (
      <Text size="xs" c="dimmed">
        Ingen data for valgt filter.
      </Text>
    );
  }

  return (
    <Group align="flex-start" wrap="nowrap" gap="md" style={{ flex: 1, minWidth: 0 }}>
      <Box w={PIE_SIZE} h={PIE_SIZE} style={{ flexShrink: 0 }}>
        <AgCharts options={options} />
      </Box>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Stack gap={4}>
          {(chartData as PieDatum[]).map((d) => {
            const percentage = totalCount > 0 ? Math.round((d.value / totalCount) * 100) : 0;

            return (
              <Group key={d.label} gap={8} align="center" wrap="nowrap">
                <Box
                  w={12}
                  h={12}
                  style={{
                    borderRadius: 3,
                    backgroundColor: d.color,
                    border: "1px solid rgba(0,0,0,0.15)",
                    flexShrink: 0,
                  }}
                />
                <Text size="xs" style={{ flex: 1, minWidth: 0 }} truncate>
                  {d.label}
                </Text>
                <Text size="xs" c="dimmed" fw={500}>
                  {d.value} ({percentage}%)
                </Text>
              </Group>
            );
          })}
        </Stack>
      </Box>
    </Group>
  );
};
